import { Box, Snackbar, Alert, Typography } from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import { ByzMuiDateTimePicker } from "src/components/core-components/mui/inputs/ByzMuiDateTimePicker";
import ByzMuiTypography from "src/components/core-components/mui/inputs/ByzMuiTypography";
import { useTranslate } from "src/locales/use-locales";
import ByzMuiTextField from "src/components/core-components/mui/inputs/ByzMuiTextField";
import ByzMuiSaveButton from "src/components/core-components/mui/buttons/ByzMuiSaveButton";
import { Fragment, useCallback, useEffect, useMemo } from "react";
import { useAppSelector } from "src/app/hooks";
import { OtherPrivilegeGuard } from "src/utils/authorization/OtherPrivilegeGuard";
import { useCardForm } from "src/hooks/useCardForm";
import ByzMcParameterDetailSelect from "src/components/shared-components/ByzMcParameterDetailSelect";
import ByzMcParameterDetailRadio from "src/components/shared-components/ByzMcParameterDetailRadio";
import { McParameterDetailResponseDto } from "src/api/references/referenceServicesDtos";

interface ImproveVTERiskFormData {
  IVRA_ID: string;
  PROCESS_TIME: Date | null;
  CTIME: Date;
  CUSER: string;
  AGE: string;
  VTE_HISTORY: string;
  THROMBOPHIA: string;
  CANCER: string;
  EXMY_PARALYSIS_OR_PARESTHESIS: string;
  IMMOBILIZATION: string;
  YBU: string;
  CALC_VALUE: string;
}

type RiskFieldKey =
  | "AGE"
  | "VTE_HISTORY"
  | "THROMBOPHIA"
  | "CANCER"
  | "EXMY_PARALYSIS_OR_PARESTHESIS"
  | "IMMOBILIZATION"
  | "YBU";

interface ImproveVTERiskProps {
  recordId?: string;
  patientId: number;
  onClose?: () => void;
  getComId: () => Promise<number | null>;
  comId: number;
  setComId: (comId: number) => void;
  getCardTableData: (comId: number) => Promise<string | "">;
  createTableMasterId: () => Promise<string | null>;
  save: (id?: string) => Promise<void | string | null>;
  handleChange: (field: keyof ImproveVTERiskFormData, value: any) => void;
  patientData: any;
  isSaving?: boolean;
  extraAccess?: boolean;
}

function ImproveVTERiskAssessmentCreate({
  recordId,
  save,
  handleChange: parentHandleChange,
  patientData,
  isSaving,
  extraAccess,
}: ImproveVTERiskProps) {
  const { t } = useTranslate();
  const { user } = useAppSelector((state) => state.auth);

  const {
    formData,
    selectedMCPs,
    loading,
    handleChange,
    handleMCPChange,
    resetForm,
    cardData,
    isEditMode,
    error,
    setError,
    getMCPParameterDetails,
    getScoreValue,
    mcpCacheLoaded,
  } = useCardForm({
    cardType: "IMPROVE_VTE_RISK_ASSESSMENT",
    recordId,
    user,
    onLoadRecord: (record) => {
      Object.entries(record).forEach(([key, value]) => {
        parentHandleChange(key as keyof ImproveVTERiskFormData, value);
      });
    },
  });

  const typedFormData = formData as unknown as ImproveVTERiskFormData;

  // Yaş otomatik seçimi
  useEffect(() => {
    if (!isEditMode && patientData?.data && mcpCacheLoaded) {
      resetForm();
      const patientInfo = patientData.data;
      const birthdate = patientInfo.birthdate;

      // Yaş hesaplama ve otomatik seçim
      if (birthdate && cardData?.mcpConfig?.AGE) {
        const birthDate = new Date(birthdate);
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
          age--;
        }

        const ageMcpId = cardData.mcpConfig.AGE.mcpId;
        const ageDetails = getMCPParameterDetails(ageMcpId);

        if (ageDetails && ageDetails.length > 0) {
          let matchedOption: McParameterDetailResponseDto | null = null;

          // Yaş > 60 ise ilgili seçeneği bul
          if (age > 60) {
            matchedOption = ageDetails.find(opt =>
              opt.name?.includes(">") && opt.name?.includes("60")
            ) || ageDetails.find(opt =>
              opt.name?.toUpperCase().includes("YAŞ") && opt.name?.includes("60")
            ) || null;
          }

          if (matchedOption) {
            handleMCPChange("AGE", matchedOption);
          }
        }
      }
    } else if (!isEditMode) {
      resetForm();
    }
  }, [isEditMode, resetForm, patientData, mcpCacheLoaded, cardData, getMCPParameterDetails, handleMCPChange]);

  useEffect(() => {
    Object.entries(formData).forEach(([key, value]) => {
      if (key !== "CALC_VALUE") {
        parentHandleChange(key as keyof ImproveVTERiskFormData, value);
      }
    });
  }, [formData, parentHandleChange]);

  // Risk faktörleri (RadioButton - Var/Yok)
  const radioFieldConfigs = useMemo<Array<{ key: RiskFieldKey; label: string }>>(
    () => [
      { key: "VTE_HISTORY", label: t("improve-vte-risk-assessment.vte-history") },
      { key: "THROMBOPHIA", label: t("improve-vte-risk-assessment.thrombophilia") },
      { key: "CANCER", label: t("improve-vte-risk-assessment.cancer") },
      { key: "EXMY_PARALYSIS_OR_PARESTHESIS", label: t("improve-vte-risk-assessment.lower-extremity-paralysis-or-paresthesia") },
      { key: "IMMOBILIZATION", label: t("improve-vte-risk-assessment.immobilization") },
      { key: "YBU", label: t("improve-vte-risk-assessment.icu-or-coronary-icu-stay") },
    ],
    [t]
  );

  // Tüm alanlar için skor hesaplama
  const allRiskFieldKeys: RiskFieldKey[] = [
    "AGE", "VTE_HISTORY", "THROMBOPHIA", "CANCER",
    "EXMY_PARALYSIS_OR_PARESTHESIS", "IMMOBILIZATION", "YBU"
  ];

  const calculateScore = useCallback(() => {
    return allRiskFieldKeys.reduce((total, key) => {
      return total + getScoreValue(key);
    }, 0);
  }, [getScoreValue]);

  useEffect(() => {
    const totalScore = calculateScore();
    const formattedScore = totalScore.toString();
    if ((typedFormData.CALC_VALUE || "") !== formattedScore) {
      handleChange("CALC_VALUE", formattedScore);
      parentHandleChange("CALC_VALUE", formattedScore);
    }
  }, [calculateScore, handleChange, typedFormData.CALC_VALUE, parentHandleChange]);

  // Skor değerlendirme metni
  const getScoreEvaluation = (score: number): { text: string; color: string } => {
    if (score <= 1) {
      return { text: "VTE Riski Düşük", color: "#4CAF50" };
    } else if (score >= 2 && score <= 3) {
      return { text: "VTE Riski Orta", color: "#FF9800" };
    } else {
      return { text: "VTE Riski Yüksek", color: "#F44336" };
    }
  };

  const scoreEvaluation = getScoreEvaluation(parseInt(typedFormData.CALC_VALUE || "0"));

  // Sabit input genişliği
  const inputContainerStyle = {
    width: "100%",
    maxWidth: "300px",
    minWidth: "200px",
    paddingRight: "16px",
    marginLeft: "4px",
  };

  // Dropdown'lar için style
  const dropdownContainerStyle = {
    width: "100%",
    maxWidth: "300px",
    minWidth: "200px",
    paddingLeft: "16px",
  };

  // MCP field render fonksiyonu
  const renderMCPField = (
    fieldName: string,
    labelKey: string,
    mcpId: number
  ) => (
    <Grid container alignItems="center" justifyContent="space-between" sx={{ minHeight: "56px" }}>
      <Grid
        xs={12}
        sm={6}
        md={6}
        sx={{ borderBottom: "1px solid #E0E0E0", display: "flex", alignItems: "center", minHeight: "56px" }}
      >
        <ByzMuiTypography sx={{ marginLeft: "30px" }}>
          {labelKey}
        </ByzMuiTypography>
      </Grid>
      <Grid xs={12} sm={6} md={6} sx={{ display: "flex", alignItems: "center", minHeight: "56px", py: 1 }}>
        <Box sx={dropdownContainerStyle}>
          <ByzMcParameterDetailSelect
            title={t("select")}
            mcpId={mcpId}
            multiple={false}
            handleChange={(val) => {
              if (val === null || val === undefined) {
                handleMCPChange(fieldName, null);
              } else if (!Array.isArray(val)) {
                const selected = val as McParameterDetailResponseDto;
                handleMCPChange(fieldName, selected);
              }
            }}
            value={selectedMCPs[fieldName]}
            sx={{ width: "100%", "& .MuiInputBase-root": { height: "40px" } }}
            cachedData={getMCPParameterDetails(mcpId)}
            cacheLoaded={mcpCacheLoaded}
          />
        </Box>
      </Grid>
    </Grid>
  );


  return (
    <>
      <Snackbar
        open={!!error?.open}
        autoHideDuration={4000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setError(null)}
          severity="error"
          sx={{ width: "100%" }}
        >
          {error?.message}
        </Alert>
      </Snackbar>

      <Box sx={{ padding: "20px", width: "100%" }}>
        <Box mb={3}>
            <OtherPrivilegeGuard
              extraAccess={!!extraAccess}
              fallback={
                <ByzMuiSaveButton
                  disabled
                  tooltip={!extraAccess ? t("no-permission-for-this-action") : undefined}
                />
              }
            >
              <ByzMuiSaveButton
                onClick={() => save(typedFormData.IVRA_ID)}
                loading={isSaving}
              />
            </OtherPrivilegeGuard>
        </Box>

        <Grid container spacing={2}>
          <Grid
            xs={12}
            sm={6}
            md={6}
            container
            direction={"column"}
            spacing={3}
          >
            {/* Başlık */}
            <Grid
              md={6}
              sx={{
                backgroundColor: "#F4F7FE",
                borderBottom: "1px solid #E0E0E0",
              }}
            >
              <ByzMuiTypography isTitle={true}>
                {t("improve-vte-risk-assessment.title")}
              </ByzMuiTypography>
            </Grid>

            {/* Tarih/Saat */}
            <Grid container alignItems="center" justifyContent="space-between" sx={{ minHeight: "56px" }}>
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0", display: "flex", alignItems: "center", minHeight: "56px" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("datetime")}
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6} sx={{ display: "flex", alignItems: "center", minHeight: "56px", py: 1 }}>
                <Box sx={inputContainerStyle}>
                  <ByzMuiDateTimePicker
                    sx={{ width: "100%", "& .MuiInputBase-root": { height: "40px" } }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: "small",
                      }
                    }}
                    label={t("datetime")}
                    value={typedFormData.PROCESS_TIME}
                    onChange={(date) => handleChange("PROCESS_TIME", date)}
                    disabled={!extraAccess}
                  />
                </Box>
              </Grid>
            </Grid>

            {/* Oluşturan Kullanıcı */}
            <Grid container alignItems="center" justifyContent="space-between" sx={{ minHeight: "56px" }}>
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0", display: "flex", alignItems: "center", minHeight: "56px" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("createdby")}
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6} sx={{ display: "flex", alignItems: "center", minHeight: "56px", py: 1 }}>
                <Box sx={inputContainerStyle}>
                  <ByzMuiTextField
                    sx={{ width: "100%", "& .MuiInputBase-root": { height: "40px" } }}
                    color="info"
                    label={t("createdby")}
                    variant="outlined"
                    size="small"
                    value={typedFormData.CUSER}
                    disabled
                  />
                </Box>
              </Grid>
            </Grid>

            {/* Mevcut Olan Risk Faktörleri Başlığı */}
            <Grid
              md={6}
              sx={{
                backgroundColor: "#F4F7FE",
                borderBottom: "1px solid #E0E0E0",
              }}
            >
              <ByzMuiTypography isTitle={true}>
                {t("improve-vte-risk-assessment.existing-risk-factors")}
              </ByzMuiTypography>
            </Grid>

            {/* Yaş - Dropdown */}
            {cardData?.mcpConfig?.AGE && (
              <Grid container alignItems="center" justifyContent="space-between" sx={{ minHeight: "56px" }}>
                <Grid
                  xs={12}
                  sm={6}
                  md={6}
                  sx={{ borderBottom: "1px solid #E0E0E0", display: "flex", alignItems: "center", minHeight: "56px" }}
                >
                  <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                    {t("improve-vte-risk-assessment.age")}
                  </ByzMuiTypography>
                </Grid>
                <Grid xs={12} sm={6} md={6} sx={{ display: "flex", alignItems: "center", minHeight: "56px", py: 1 }}>
                  <Box sx={inputContainerStyle}>
                    <ByzMcParameterDetailSelect
                      title={t("select")}
                      mcpId={cardData.mcpConfig.AGE.mcpId}
                      multiple={false}
                      handleChange={(val) => {
                        if (val === null || val === undefined) {
                          handleMCPChange("AGE", null);
                        } else if (!Array.isArray(val)) {
                          const selected = val as McParameterDetailResponseDto;
                          handleMCPChange("AGE", selected);
                        }
                      }}
                      value={selectedMCPs.AGE}
                      sx={{ width: "100%", "& .MuiInputBase-root": { height: "40px" } }}
                      cachedData={getMCPParameterDetails(cardData.mcpConfig.AGE.mcpId)}
                      cacheLoaded={mcpCacheLoaded}
                      readOnly={!extraAccess}
                    />
                  </Box>
                </Grid>
              </Grid>
            )}

            {/* Diğer risk faktörleri - RadioButton (Var/Yok) */}
            {radioFieldConfigs.map(({ key, label }) => {
              const mcpConfig = cardData?.mcpConfig?.[key];
              if (!mcpConfig) return null;

              return (
                <Grid key={key} container alignItems="center" justifyContent="space-between" sx={{ minHeight: "56px" }}>
                  <Grid
                    xs={12}
                    sm={6}
                    md={6}
                    sx={{ borderBottom: "1px solid #E0E0E0", display: "flex", alignItems: "center", minHeight: "56px" }}
                  >
                    <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                      {label}
                    </ByzMuiTypography>
                  </Grid>
                  <Grid xs={12} sm={6} md={6} sx={{ display: "flex", alignItems: "center", minHeight: "56px", py: 1 }}>
                    <Box sx={{ paddingLeft: "16px", width: "100%" }}>
                      <ByzMcParameterDetailRadio
                        mcpId={mcpConfig.mcpId}
                        row={true}
                        reverseOrder={true}
                        value={selectedMCPs[key]}
                        handleChange={(val) => {
                          if (val) {
                            handleMCPChange(key, val);
                          } else {
                            handleMCPChange(key, null);
                          }
                        }}
                        cachedData={getMCPParameterDetails(mcpConfig.mcpId)}
                        cacheLoaded={mcpCacheLoaded}
                        disabled={!extraAccess}
                      />
                    </Box>
                  </Grid>
                </Grid>
              );
            })}

            {/* Diğer Başlığı */}
            <Grid
              md={6}
              sx={{
                backgroundColor: "#F4F7FE",
                borderBottom: "1px solid #E0E0E0",
              }}
            >
              <ByzMuiTypography isTitle={true}>
                {t("other")}
              </ByzMuiTypography>
            </Grid>

            {/* Skor */}
            <Grid container alignItems="center" justifyContent="space-between" sx={{ minHeight: "56px" }}>
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0", display: "flex", alignItems: "center", minHeight: "56px" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("score")}
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6} sx={{ display: "flex", alignItems: "center", minHeight: "56px", py: 1 }}>
                <Box sx={inputContainerStyle}>
                  <ByzMuiTextField
                    sx={{ width: "100%", "& .MuiInputBase-root": { height: "40px" } }}
                    label={t("score")}
                    size="small"
                    value={typedFormData.CALC_VALUE || "0"}
                    disabled
                  />
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </>
  );
}

export default ImproveVTERiskAssessmentCreate;
